import React, { useEffect, useState } from "react";
import { useCombobox } from "downshift";
import Form from "react-bootstrap/Form";
import { Button } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { useDispatch, useMappedState } from "redux-react-hook";
import { useAsyncDebounce } from "react-table";
import { priceTagConstants } from "../constants/priceTagConstants";
import { PriceTag } from "../reducers/priceTags";

type Props = {
  name?: string;
  handleSubscriptionUpdate: (value) => void;
};

const PriceTagsAutocomplete: React.FC<Props> = ({ handleSubscriptionUpdate, name }) => {
  const [value, setValue] = useState(name || "");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const items: PriceTag[] = useMappedState((state) => state.priceTags.priceTags);
  const isError = useMappedState((state: any) => state.errors.isError);

  useEffect(() => {
    if (items || isError) {
      setLoading(false);
    }
  }, [items, isError]);

  const handleChange = useAsyncDebounce(async () => {
    if (value.length < 3) return;
    setLoading(true);
    dispatch({
      type: priceTagConstants.GET_PRICE_TAGS,
      payload: {
        name: value,
      },
    });
  }, 1000);

  const { isOpen, getMenuProps, getInputProps, getComboboxProps, highlightedIndex, getItemProps } = useCombobox({
    items,
    inputValue: value,
    itemToString: (item) => (item && item?.name) || "",
    onSelectedItemChange: (changes) => {
      const { selectedItem } = changes;
      if (selectedItem?.name) {
        setValue(selectedItem?.name);
        handleSubscriptionUpdate({
          priceTag:
            { id: selectedItem?.id, name: selectedItem?.name },
        });
      }
    },
    onInputValueChange: async ({ inputValue }) => {
      setValue(inputValue || "");
      await handleChange();
    },
  });

  const removePriceTag = () => {
    handleSubscriptionUpdate({ priceTag: { id: null, name: null } });
    setValue("");
  };

  return (
    <div className="flex">
      <div {...getComboboxProps()}>
        <div className="d-flex">
          <Form.Control
            {...getInputProps()}
          />
          {loading && (
            <Spinner className="align-self-center ml-2" as="span" animation="border" size="sm" role="status" />
          )}
        </div>
        <ul
          {...getMenuProps()}
          className="max-h-[180px] overflow-y-auto border-t-0 bg-white absolute z-50 list-none p-0 left-auto w-[250px]"
        >
          {isOpen &&
            items.map((item, i) => (
              <li
                className={
                  `bg-${highlightedIndex === i ? "blue-100" : "white"} 
                  text-${loading ? "gray-500" : "inherit"} cursor-pointer`
                }
                key={item.id}
                {...getItemProps({ item, index: i })}
              >
                {item.name}
              </li>
            ))}
        </ul>
      </div>
      {value && (
        <Button onClick={removePriceTag} className="mt-2">
          Remove price tag
        </Button>
      )}
    </div>
  );
};

export default PriceTagsAutocomplete;
